import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { CtaBand } from "@/components/blocks";
import { AudioPlayer } from "@/components/AudioPlayer";
import { Reveal } from "@/components/Reveal";
import { breadcrumbJsonLd } from "@/lib/seo";

const LANG_WORD_TO_CODE = { english: "en", malayalam: "ml", hindi: "hi" };
const LANG_CODE_TO_WORD = { en: "english", ml: "malayalam", hi: "hindi" };
const LANG_LABELS = { en: "English", ml: "Malayalam", hi: "Hindi" };

export default function PodcastEpisode() {
  const { slug, langWord } = useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [relatedArticle, setRelatedArticle] = useState(null);

  const episodeNumber = parseInt((slug || "").replace(/^episode-/, ""), 10);

  useEffect(() => {
    setEpisode(null);
    setNotFound(false);
    setRelatedArticle(null);
    if (!episodeNumber || Number.isNaN(episodeNumber)) {
      setNotFound(true);
      return;
    }
    api.get(`/podcasts/${episodeNumber}`)
      .then(({ data }) => setEpisode(data))
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeNumber]);

  useEffect(() => {
    if (!episode || episode.available_languages.length === 0) return;
    const code = LANG_WORD_TO_CODE[langWord];
    if (!code || !episode.available_languages.includes(code)) {
      navigate(`/podcasts/${episode.slug}/${LANG_CODE_TO_WORD[episode.available_languages[0]]}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode, langWord]);

  useEffect(() => {
    if (episode?.related_article_slug) {
      api.get(`/articles/${episode.related_article_slug}`)
        .then(({ data }) => setRelatedArticle(data))
        .catch(() => setRelatedArticle(null));
    }
  }, [episode?.related_article_slug]);

  if (notFound) {
    return (
      <div className="container-site section-pad text-center" data-testid="podcast-episode-not-found">
        <h1 className="h2-display mb-4">Episode Not Found</h1>
        <p className="lead-text mb-6">This episode may not be published yet, or may have been moved.</p>
        <button onClick={() => navigate("/podcasts")} className="btn-primary">BACK TO PODCASTS</button>
      </div>
    );
  }

  if (!episode || episode.available_languages.length === 0) return null;

  const activeCode = LANG_WORD_TO_CODE[langWord] || episode.available_languages[0];
  const edition = episode.editions[activeCode];
  if (!edition) return null;

  const crumbs = [{ label: "Podcasts", to: "/podcasts" }, { label: edition.title }];

  return (
    <>
      <Seo
        title={`${edition.title} | CTS Business Insights Podcast`}
        description={edition.description || episode.description}
        image={episode.cover_image}
        jsonLd={breadcrumbJsonLd(crumbs)}
      />

      <section className="bg-white border-b border-cts-line">
        <div className="container-site section-pad !pb-10 !pt-10 md:!pt-14">
          <Link to="/podcasts" data-testid="podcast-episode-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-cts-charcoal hover:text-cts-red mb-6">
            <ArrowLeft size={15} /> All Episodes
          </Link>
          <div className="grid gap-10 lg:grid-cols-[320px_1fr] items-start">
            <Reveal>
              <div className="rounded-xl overflow-hidden border border-cts-line shadow-[0_30px_60px_-30px_rgba(23,27,30,0.35)] bg-cts-charcoal aspect-square max-w-[200px] mx-auto lg:max-w-none">
                {episode.cover_image ? (
                  <img src={episode.cover_image} alt={episode.title} className="w-full h-full object-cover" data-testid="podcast-episode-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40 font-heading font-bold text-xs uppercase">
                    CTS Podcast
                  </div>
                )}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <p className="eyebrow mb-3">CTS BUSINESS INSIGHTS · EPISODE {episode.episode_number}</p>
              <h1 className="h1-display">{edition.title}</h1>
              {edition.description && <p className="lead-text mt-5">{edition.description}</p>}

              {episode.available_languages.length > 1 && (
                <div className="flex flex-wrap gap-2 mt-6" role="tablist" aria-label="Language edition" data-testid="podcast-language-selector">
                  {episode.available_languages.map((code) => (
                    <Link
                      key={code}
                      to={`/podcasts/${episode.slug}/${LANG_CODE_TO_WORD[code]}`}
                      role="tab"
                      aria-selected={code === activeCode}
                      data-testid={`podcast-lang-${code}`}
                      className={`rounded-md px-4 py-2 text-sm font-heading font-bold uppercase tracking-wide transition-colors ${
                        code === activeCode ? "bg-cts-red text-white" : "bg-cts-soft text-cts-charcoal hover:text-cts-red"
                      }`}
                    >
                      {LANG_LABELS[code]}
                    </Link>
                  ))}
                </div>
              )}

              <div className="mt-7">
                <AudioPlayer src={edition.audio_url} title={edition.title} testId="podcast-audio-player" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {edition.transcript && (
        <section className="section-pad bg-cts-soft" data-testid="podcast-transcript">
          <div className="container-site max-w-3xl">
            <h2 className="h3-display mb-6 flex items-center gap-2"><FileText size={22} className="text-cts-red" /> Transcript</h2>
            <div className="card-base p-6 md:p-8">
              {edition.transcript.split(/\n\s*\n/).map((para, i) => (
                <p key={i} className="lead-text !text-cts-charcoal mb-4 last:mb-0">{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {relatedArticle && (
        <section className="section-pad" data-testid="podcast-related-article">
          <div className="container-site max-w-3xl">
            <h2 className="h3-display mb-6">Related Technology Advice</h2>
            <Link to={`/technology-advice/${relatedArticle.slug}`} data-testid="podcast-related-article-link" className="card-base flex items-center justify-between gap-4 p-6 hover:!border-cts-red">
              <div>
                <p className="font-heading font-bold text-cts-ink">{relatedArticle.title}</p>
                {relatedArticle.excerpt && <p className="text-sm text-cts-charcoal mt-1">{relatedArticle.excerpt}</p>}
                <span className="link-arrow mt-2 !text-cts-red text-xs font-bold uppercase">READ ARTICLE</span>
              </div>
              <ArrowUpRight size={20} className="text-cts-red shrink-0" />
            </Link>
          </div>
        </section>
      )}

      <CtaBand title="Have a Question About This Episode?" requirement="Other" />
    </>
  );
}
